import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPost, fmtCurrency, fmtDate } from "@/utils/api";
import { StatusBadge } from "@/components/StatusBadge";
import {
  AlertCircle, ArrowRight, CheckCircle2, FileText,
  PackageCheck, RefreshCw, Send, ShieldAlert, Activity, Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const KPI_CONFIG = [
  { key: "total_invoices", label: "Total invoices", icon: FileText, tone: "slate" },
  { key: "released", label: "Released to SAP", icon: CheckCircle2, tone: "teal" },
  { key: "errors", label: "Posting exceptions", icon: ShieldAlert, tone: "red" },
];

const TONE_CLASSES = {
  slate: "border-slate-200 text-slate-800 bg-slate-100",
  amber: "border-amber-200 text-amber-800 bg-amber-50",
  teal: "border-teal-200 text-teal-800 bg-teal-50",
  red: "border-red-200 text-red-800 bg-red-50",
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [actionNeeded, setActionNeeded] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [fifoBlockers, setFifoBlockers] = useState(null);
  const [fifoExpanded, setFifoExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState({});
  const navigate = useNavigate();

  const fetchAll = useCallback(async () => {
    const [summaryR, actionsR, activityR, fifoR] = await Promise.allSettled([
      apiGet("/dashboard/stats"),
      apiGet("/dashboard/action-needed"),
      apiGet("/audit-log?limit=8"),
      apiGet("/dashboard/po-item-fifo-blockers"),
    ]);
    if (summaryR.status === "fulfilled") setStats(summaryR.value);
    if (actionsR.status === "fulfilled") setActionNeeded(actionsR.value);
    if (activityR.status === "fulfilled") setAuditLogs(activityR.value.logs || []);
    if (fifoR.status === "fulfilled") setFifoBlockers(fifoR.value);
    else toast.error(`PO-item FIFO panel failed to load: ${fifoR.reason?.message || "unknown error"}`);
    const coreFailure = [summaryR, actionsR, activityR].find((r) => r.status === "rejected");
    if (coreFailure) toast.error(`Dashboard load failed: ${coreFailure.reason?.message || "unknown error"}`);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Group the raw per-(PO, item) combo list by its oldest (actionable)
  // shipment — finance clears ONE shipment at a time, not one PO-item line
  // at a time, and the same two shipments are typically paired across
  // several shared PO items at once.
  const fifoGroups = useMemo(() => {
    const combos = fifoBlockers?.combos || [];
    const byOldest = new Map();
    for (const combo of combos) {
      const key = combo.oldest_shipment.shipment_code;
      if (!byOldest.has(key)) {
        byOldest.set(key, {
          shipment: combo.oldest_shipment,
          vendorName: combo.vendor_name,
          poItems: [],
          blockedShipments: new Map(),
        });
      }
      const group = byOldest.get(key);
      group.poItems.push(`${combo.po_number}/${combo.po_item_number}`);
      for (const blocked of combo.blocked_shipments) {
        group.blockedShipments.set(blocked.shipment_code, blocked);
      }
    }
    return [...byOldest.values()]
      .map((group) => ({ ...group, blockedShipments: [...group.blockedShipments.values()] }))
      .sort((a, b) => (a.shipment.receipt_date || "").localeCompare(b.shipment.receipt_date || ""));
  }, [fifoBlockers]);

  const retryPost = async (invoiceId) => {
    setRetrying((current) => ({ ...current, [invoiceId]: "post" }));
    try {
      await apiPost(`/invoices/${invoiceId}/post`, {});
      toast.success("Invoice re-posted to SAP");
      fetchAll();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setRetrying((current) => ({ ...current, [invoiceId]: false }));
    }
  };

  const actionItems = [
    ...(actionNeeded?.error_invoices || []).map((invoice) => ({
      id: invoice.invoice_id,
      type: "SAP posting exception",
      reference: invoice.invoice_id,
      details: invoice.sap_error_message || "SAP posting needs a retry",
      status: invoice.status,
      action: "Retry post",
      onAction: () => retryPost(invoice.invoice_id),
      retrying: retrying[invoice.invoice_id],
    })),
    ...(actionNeeded?.pending_boes || []).map((boe) => ({
      id: boe.boe_id,
      type: "Customs reconciliation",
      reference: boe.boe_number,
      details: `${boe.shipment_code} · Duty paid ${fmtCurrency(boe.total_duty_paid)}`,
      status: "pending_reconciliation",
      action: "Review BOE",
      onAction: () => navigate("/supplementary-invoices/customs-duty"),
    })),
  ];

  if (loading) {
    return <DashboardLoading />;
  }

  return (
    <div className="max-w-[1500px] mx-auto space-y-6" data-testid="dashboard-page">
      <section className="border border-teal-100 bg-white overflow-hidden" data-testid="dashboard-overview">
        <div className="h-1 bg-[#0A838A]" />
        <div className="px-5 py-5 sm:px-7 sm:py-6 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] uppercase font-bold tracking-wide text-[#0A838A]">Operations control</p>
            <h2 className="mt-1 text-2xl sm:text-3xl font-heading font-bold text-slate-900">Supplier finance overview</h2>
            <p className="mt-2 text-sm text-slate-600">Invoice release, PO-item FIFO holds, and customs reconciliation in one working view.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="px-3 py-2 border-l-2 border-[#0A838A] bg-[#F2F9F8]" data-testid="action-count-summary">
              <p className="text-xl leading-none font-bold text-[#076D74]">{actionItems.length}</p>
              <p className="mt-1 text-[11px] font-bold text-slate-600 uppercase">Open actions</p>
            </div>
            <Button
              onClick={() => navigate("/invoices/new")}
              data-testid="create-invoice-button"
              className="bg-[#0A838A] hover:bg-[#076D74] text-white font-bold h-10 px-4"
            >
              Create invoice <ArrowRight size={15} />
            </Button>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-3 border border-slate-200 bg-white" data-testid="kpi-grid">
        {KPI_CONFIG.map(({ key, label, icon: Icon, tone }, index) => (
          <div key={key} className={`p-4 sm:p-5 ${index < KPI_CONFIG.length - 1 ? "border-b sm:border-b-0 sm:border-r border-slate-200" : ""}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase font-bold text-slate-500">{label}</p>
                <p className="mt-3 text-3xl font-bold text-slate-900 tabular-nums" data-testid={`kpi-${key}`}>
                  {stats?.[key] ?? 0}
                </p>
              </div>
              <div className={`w-9 h-9 border flex items-center justify-center ${TONE_CLASSES[tone]}`}>
                <Icon size={17} />
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="border border-slate-200 bg-white" data-testid="po-item-fifo-panel">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 border border-amber-200 bg-amber-50 text-amber-700 flex items-center justify-center shrink-0">
              <Lock size={15} />
            </div>
            <div>
              <p className="text-[11px] uppercase font-bold tracking-wide text-[#0A838A]">FIFO guard</p>
              <h3 className="mt-0.5 text-base font-heading font-bold text-slate-900">PO-item FIFO holds</h3>
            </div>
          </div>
          <p className="text-[11px] font-bold text-slate-500" data-testid="fifo-blocked-shipment-count">
            {fifoBlockers?.blocked_shipment_count ?? 0} shipment(s) waiting
          </p>
        </div>
        {fifoGroups.length ? <FifoBlockerList groups={fifoExpanded ? fifoGroups : fifoGroups.slice(0, 6)} navigate={navigate} /> : <AllClearFifo />}
        {fifoGroups.length > 6 && (
          <div className="px-5 py-3 border-t border-slate-200 text-center">
            <Button
              variant="ghost" size="sm"
              onClick={() => setFifoExpanded((v) => !v)}
              data-testid="fifo-show-all-toggle"
              className="font-bold text-[#0A838A] hover:bg-[#F2F9F8] text-xs"
            >
              {fifoExpanded ? "Show fewer" : `Show all ${fifoGroups.length} shipments`}
            </Button>
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <section className="xl:col-span-8 border border-slate-200 bg-white" data-testid="action-needed-panel">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
            <div>
              <p className="text-[11px] uppercase font-bold tracking-wide text-[#0A838A]">Priority queue</p>
              <h3 className="mt-0.5 text-base font-heading font-bold text-slate-900">Action needed</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate("/invoices/new")} data-testid="view-all-invoices-button" className="font-bold text-slate-700 hover:bg-slate-100">
              All invoices <ArrowRight size={14} />
            </Button>
          </div>
          {actionItems.length ? <ActionQueue items={actionItems} /> : <AllClear />}
        </section>

        <aside className="xl:col-span-4 border border-slate-200 bg-white" data-testid="recent-activity-panel">
          <div className="px-5 py-4 border-b border-slate-200">
            <div className="flex items-center gap-2 text-[#0A838A]">
              <Activity size={16} />
              <p className="text-[11px] uppercase font-bold tracking-wide">Live record</p>
            </div>
            <h3 className="mt-1 text-base font-heading font-bold text-slate-900">Recent activity</h3>
          </div>
          <div className="divide-y divide-slate-100" data-testid="audit-feed">
            {auditLogs.length ? auditLogs.map((log, index) => (
              <div key={log.log_id || index} className="px-5 py-3.5 flex gap-3">
                <span className={`mt-1.5 w-2 h-2 shrink-0 ${log.status === "success" ? "bg-[#0A838A]" : "bg-red-500"}`} />
                <div className="min-w-0">
                  <p className="text-[13px] font-bold text-slate-800 truncate">{log.action_type?.replace(/_/g, " ")}</p>
                  <p className="mt-0.5 text-[12px] text-slate-500 truncate">{log.entity_ref} · {log.user_email?.split("@")[0]}</p>
                  <p className="mt-1 text-[11px] text-slate-400">{fmtDate(log.timestamp)}</p>
                </div>
              </div>
            )) : <p className="px-5 py-12 text-center text-sm text-slate-500" data-testid="empty-audit-message">No recent activity</p>}
          </div>
        </aside>
      </div>
    </div>
  );
}

function ActionQueue({ items }) {
  return (
    <>
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full min-w-[680px] text-left">
          <thead className="bg-slate-200 border-b-2 border-slate-300">
            <tr>
              {['Work item', 'Reference', 'Context', 'Status', ''].map((heading) => (
                <th key={heading || 'action'} className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-slate-700">{heading}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-300">
            {items.map((item) => <QueueRow key={`${item.type}-${item.id}`} item={item} />)}
          </tbody>
        </table>
      </div>
      <div className="md:hidden divide-y divide-slate-100">
        {items.map((item) => (
          <article key={`${item.type}-${item.id}`} className="p-4" data-testid={`action-item-${item.id}`}>
            <p className="text-[11px] uppercase font-bold text-[#0A838A]">{item.type}</p>
            <p className="mt-1 text-sm font-bold text-slate-900">{item.reference}</p>
            <p className="mt-1 text-xs text-slate-500">{item.details}</p>
            <div className="mt-3 flex items-center justify-between gap-3">
              <StatusBadge status={item.status} />
              <QueueButton item={item} />
            </div>
          </article>
        ))}
      </div>
    </>
  );
}

function QueueRow({ item }) {
  return (
    <tr className="hover:bg-slate-50/70" data-testid={`action-item-${item.id}`}>
      <td className="px-4 py-3 text-[13px] font-bold text-slate-800">{item.type}</td>
      <td className="px-4 py-3 text-[13px] font-bold text-[#0A838A]">{item.reference}</td>
      <td className="max-w-[280px] px-4 py-3 text-[13px] text-slate-500 truncate">{item.details}</td>
      <td className="px-4 py-3"><StatusBadge status={item.status} /></td>
      <td className="px-4 py-3 text-right"><QueueButton item={item} /></td>
    </tr>
  );
}

function QueueButton({ item }) {
  return (
    <Button
      size="sm"
      onClick={item.onAction}
      disabled={Boolean(item.retrying)}
      data-testid={`action-button-${item.id}`}
      className="h-8 bg-[#0A838A] hover:bg-[#076D74] text-white font-bold text-xs"
    >
      {item.retrying ? <RefreshCw size={13} className="animate-spin" /> : item.action === "Review BOE" ? <Send size={13} /> : <RefreshCw size={13} />}
      {item.retrying ? "Working…" : item.action}
    </Button>
  );
}

function FifoBlockerList({ groups, navigate }) {
  // Direct prepare-page route (same pattern used by every other "open this
  // shipment" link in the app — NewInvoice/PrepareInvoice/ThreeWayException
  // FIFO blocker buttons). Going through /invoices/new?source=shipment
  // instead would flash the full worklist register table for a moment
  // while it resolves the source, then redirect here anyway.
  const goToShipment = (code) => navigate(`/invoices/prepare/direct-${encodeURIComponent(code)}`);
  return (
    <div className="divide-y divide-slate-100">
      {groups.map((group) => (
        <article key={group.shipment.shipment_code} className="px-5 py-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between" data-testid={`fifo-group-${group.shipment.shipment_code}`}>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-[13px] font-bold text-[#0A838A]">{group.shipment.shipment_code}</span>
              <span className="text-[11px] font-bold text-slate-400">·</span>
              <span className="text-[12px] text-slate-600 truncate">{group.vendorName || "Unknown vendor"}</span>
              <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase bg-amber-50 text-amber-700 border border-amber-200">
                {group.poItems.length} PO item{group.poItems.length !== 1 ? "s" : ""} held up
              </span>
            </div>
            <p className="mt-1.5 text-[12px] text-slate-500">
              Received {fmtDate(group.shipment.receipt_date)} · blocking{" "}
              <span className="font-bold text-slate-700" data-testid={`fifo-group-blocked-list-${group.shipment.shipment_code}`}>
                {group.blockedShipments.map((b) => b.shipment_code).join(", ")}
              </span>
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => goToShipment(group.shipment.shipment_code)}
            data-testid={`fifo-go-to-shipment-${group.shipment.shipment_code}`}
            className="h-8 shrink-0 bg-[#0A838A] hover:bg-[#076D74] text-white font-bold text-xs"
          >
            Go to shipment <ArrowRight size={13} />
          </Button>
        </article>
      ))}
    </div>
  );
}

function AllClearFifo() {
  return (
    <div className="px-5 py-10 text-center" data-testid="fifo-all-clear-message">
      <div className="mx-auto w-11 h-11 flex items-center justify-center bg-[#E7F5F5] text-[#0A838A]">
        <PackageCheck size={21} />
      </div>
      <p className="mt-3 text-sm font-bold text-slate-800">No PO-item FIFO holds right now</p>
      <p className="mt-1 text-[13px] text-slate-500">Every open shipment is clear to invoice on its own PO items.</p>
    </div>
  );
}


function AllClear() {
  return (
    <div className="px-5 py-14 text-center" data-testid="all-clear-message">
      <div className="mx-auto w-11 h-11 flex items-center justify-center bg-[#E7F5F5] text-[#0A838A]">
        <PackageCheck size={21} />
      </div>
      <p className="mt-3 text-sm font-bold text-slate-800">The queue is clear</p>
      <p className="mt-1 text-[13px] text-slate-500">There are no invoices or customs entries waiting for action.</p>
    </div>
  );
}

function DashboardLoading() {
  return (
    <div className="h-64 flex flex-col items-center justify-center gap-3" data-testid="dashboard-loading-state">
      <div className="w-7 h-7 border-2 border-[#0A838A] border-t-transparent animate-spin" />
      <p className="text-sm font-bold text-slate-500">Loading operations overview</p>
    </div>
  );
}